import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { surveysApi, IntakeLinkData, IntakeLinkResponse } from '@/api/surveys';
import AssignSuccessStep from '@/components/assignments/steps/AssignSuccessStep';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { useModalContext } from '@/components/ui/modal-provider';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { getSharableSurveyText } from '@/pages/surveys/utils/getSurveyText';

const INTAKE_SURVEY_NAME = 'Pré-anamnese adulto | Neuropsicologia';

interface IntakeSurveyLinkModalProps {
    patientName?: string;
}

export default function IntakeSurveyLinkModal({ patientName }: IntakeSurveyLinkModalProps) {
    const { closeModal } = useModalContext();
    const queryClient = useQueryClient();
    const hasRequestedRef = useRef(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [intakeLink, setIntakeLink] = useState<IntakeLinkData | null>(null);
    const normalizedName = patientName?.trim();
    const patientLinkAccess = useFeatureEntitlement('patientLinks.send');

    const {
        data: surveys = [],
        isLoading: isLoadingSurveys,
        isFetching: isFetchingSurveys,
    } = useQuery({
        queryKey: ['surveys'],
        queryFn: surveysApi.list,
    });

    const { mutate: createLink, isPending: isCreating } = useMutation({
        mutationFn: ({ surveyId, alias }: { surveyId: string; alias: string }) =>
            surveysApi.createIntakeLink(surveyId, alias),
        onSuccess: (response: IntakeLinkResponse) => {
            setIntakeLink(response.data);
            queryClient.invalidateQueries({ queryKey: ['patients'] });
        },
        onError: () => {
            setErrorMessage('Não foi possível gerar o link. Tente novamente.');
            toast.error('Erro ao gerar link do formulário.');
        },
    });

    const intakeSurvey = useMemo(() => {
        return surveys.find((survey) => survey.name === INTAKE_SURVEY_NAME);
    }, [surveys]);

    useEffect(() => {
        if (patientLinkAccess.shouldBlock) return;
        if (isLoadingSurveys || isFetchingSurveys || isCreating || intakeLink) return;
        if (hasRequestedRef.current) return;

        if (!intakeSurvey) {
            setErrorMessage('Formulário padrão não encontrado.');
            toast.error('Formulário padrão não encontrado.');
            hasRequestedRef.current = true;
            return;
        }

        hasRequestedRef.current = true;
        createLink({
            surveyId: intakeSurvey.id,
            alias: normalizedName || '',
        });
    }, [
        isLoadingSurveys,
        isFetchingSurveys,
        isCreating,
        intakeLink,
        intakeSurvey,
        createLink,
        normalizedName,
        patientLinkAccess.shouldBlock,
    ]);

    const getFormattedExpiryDate = (value?: string) => {
        if (!value) return '';
        const expiryDate = new Date(value);
        if (Number.isNaN(expiryDate.getTime())) return '';

        return new Intl.DateTimeFormat('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(expiryDate);
    };

    if (patientLinkAccess.shouldBlock) {
        return (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">
                    {getEntitlementBlockedMessage(patientLinkAccess.decision)}
                </p>
                {patientLinkAccess.isUnavailableInPlan && patientLinkAccess.availableFromPlan ? (
                    <FeatureUnavailableChip availablePlan={patientLinkAccess.availableFromPlan} />
                ) : null}
                <Button variant="outline" onClick={closeModal}>
                    Fechar
                </Button>
            </div>
        );
    }

    if (isLoadingSurveys || isFetchingSurveys || (isCreating && !intakeLink)) {
        return (
            <div className="py-10">
                <Loading className="h-40" size={120} />
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">{errorMessage}</p>
                <Button variant="outline" onClick={closeModal}>
                    Fechar
                </Button>
            </div>
        );
    }

    if (!intakeLink) return null;

    const shareLink = `${window.location.origin}/patients/survey/answer?token=${intakeLink.unlock_token}`;
    const shareText = getSharableSurveyText(shareLink);

    const title = normalizedName
        ? `Formulário pronto para ${normalizedName}`
        : 'Formulário pronto para envio';

    return (
        <AssignSuccessStep
            title={title}
            description="O link de acesso foi gerado e está pronto para compartilhamento."
            expiryDate={getFormattedExpiryDate(intakeLink.unlock_token_expires_at)}
            shareLink={shareLink}
            shareText={shareText}
        />
    );
}
